/**
 * TopRibbon - the slim desktop chrome bar above the workspace.
 *
 * Three zones (Rick's spec): left = context label (the deal / inbox name for
 * orientation), center = a search affordance that opens the Cmd-K palette,
 * right = the Neo open button (Cmd-/). Desktop only; the mobile nav already
 * carries the command-palette + Neo affordances so the ribbon is hidden there.
 *
 * The center "search" is a button styled like a field, not a live input, so it
 * routes into the existing Cmd-K palette (one search surface, no duplicate
 * query state).
 */
import React, { useState } from 'react';
import { PhoneCall, Search, Sparkles } from 'lucide-react';
import QuickNotesButton from '../notes/QuickNotesButton.js';
import VacationIndicator from './VacationIndicator.js';
import GlobalDialer from '../calls/GlobalDialer.js';

export default function TopRibbon({
  contextLabel,
  onOpenSearch,
  onToggleNeo,
  neoOpen,
  contextAction,
}: {
  contextLabel: string;
  onOpenSearch: () => void;
  onToggleNeo: () => void;
  neoOpen: boolean;
  /** Route-provided filters/context trigger. Populated only on narrow (tablet)
   *  viewports where the three-pane context column is hidden - without it the
   *  768-1023px band had no way to reach inbox folders/accounts/segments. */
  contextAction?: React.ReactNode;
}) {
  const [dialerOpen, setDialerOpen] = useState(false);

  return (
    <header
      role="banner"
      className="flex h-11 flex-shrink-0 items-center gap-3 border-b border-line bg-surface px-3"
    >
      {/* Left: context label (+ the narrow-viewport filters trigger) */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {contextAction}
        <span className="truncate text-sm font-semibold text-ink" title={contextLabel}>
          {contextLabel}
        </span>
      </div>

      {/* Center: search / Cmd-K entry (opens the palette, not a live input) */}
      <button
        type="button"
        onClick={onOpenSearch}
        aria-label="Open search (Command K)"
        className="group flex h-8 w-full max-w-md items-center gap-2 rounded-md border border-line bg-surface-sunken/60 px-3 text-left text-sm text-ink-subtle transition-colors duration-150 hover:bg-surface-sunken hover:text-ink-muted"
      >
        <Search size={14} className="flex-shrink-0" />
        <span className="flex-1 truncate">Search deals, conversations, contacts...</span>
        <kbd className="hidden flex-shrink-0 rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[10px] text-ink-subtle sm:inline-block">
          {'⌘'}K
        </kbd>
      </button>

      {/* Right: quick-note capture + Neo open button */}
      <div className="flex min-w-0 flex-1 items-center justify-end gap-1">
        <VacationIndicator />
        <QuickNotesButton />
        <button
          type="button"
          onClick={() => setDialerOpen(true)}
          title="Open dialer"
          className="flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-ink-muted transition-colors duration-150 hover:bg-surface-raised hover:text-ink"
        >
          <PhoneCall size={15} />
          <span className="hidden md:inline">Call</span>
        </button>
        <button
          type="button"
          onClick={onToggleNeo}
          aria-pressed={neoOpen}
          title="Open Neo (Command /)"
          className={`flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors duration-150 ${
            neoOpen
              ? 'bg-brand/10 text-brand'
              : 'text-ink-muted hover:bg-surface-raised hover:text-ink'
          }`}
        >
          <Sparkles size={15} />
          <span className="hidden md:inline">Neo</span>
          <kbd className="hidden rounded border border-line bg-surface px-1 py-0.5 font-mono text-[10px] text-ink-subtle lg:inline-block">
            {'⌘'}/
          </kbd>
        </button>
      </div>
      <GlobalDialer open={dialerOpen} onClose={() => setDialerOpen(false)} />
    </header>
  );
}
